import { Activity, ShieldCheck, Zap, Sparkles, Moon, HeartPulse, FlaskConical } from "lucide-react";

// Shopper-facing "goal" layer over the stored product categories (display only — data/admin unchanged).
export const GOALS = [
  {
    slug: "weight-management", image: "/goals/weight-management.jpg", category: "Weight Loss", name: "Weight Management",
    tagline: "Peptides studied in metabolic and appetite-signalling research.",
    blurb: "GLP-1 and GIP receptor agonists such as Tirzepatide and Retatrutide are among the most studied compounds in metabolic research. Each listing links to its research guide and certificate of analysis.",
    icon: Activity, gradient: "from-[#F4EFEA] to-[#EBE4DC]", border: "#D8CEBE", iconColor: "#7A6B5D", badgeBg: "#EFE8DF", badgeText: "#5C4E42",
  },
  {
    slug: "repair-recovery", image: "/goals/repair-recovery.jpg", category: "Recovery & Healing", name: "Repair & Recovery",
    tagline: "Compounds researched in tissue-repair and recovery models.",
    blurb: "BPC-157, TB-500 and related peptides appear widely in preclinical tissue-repair literature. Browse the guides for what each compound is, what it is being studied for and where the evidence stands.",
    icon: ShieldCheck, gradient: "from-[#EDF3EE] to-[#DEE8E0]", border: "#C4D6C7", iconColor: "#2D6A4F", badgeBg: "#E2ECE4", badgeText: "#1E4A37",
  },
  {
    slug: "performance-growth", image: "/goals/performance-growth.jpg", category: "Growth Hormone", name: "Performance & Growth",
    tagline: "Growth-hormone-axis peptides studied in performance research.",
    blurb: "Secretagogues like CJC-1295 and Ipamorelin, and growth factors such as IGF-1 LR3, are studied for their role in the GH/IGF-1 axis. Research use only — no protocols or dosing are provided.",
    icon: Zap, gradient: "from-[#F5EFE6] to-[#E9DDD0]", border: "#DBC7B5", iconColor: "#8C6A4B", badgeBg: "#EFE3D5", badgeText: "#5E442F",
  },
  {
    slug: "anti-aging-longevity", image: "/goals/anti-aging-longevity.jpg", category: "Skin & Anti-Aging", name: "Anti-Aging & Longevity",
    tagline: "Studied in cellular-ageing, skin and mitochondrial research.",
    blurb: "GHK-Cu, NAD+ and Glutathione are long-standing subjects of skin, redox and cellular-ageing research. Every vial ships with a lot number and third-party certificate of analysis.",
    icon: Sparkles, gradient: "from-[#F3EEF5] to-[#E4DAE8]", border: "#CFBFD6", iconColor: "#6B4C7A", badgeBg: "#EAE0EF", badgeText: "#4A3256",
  },
  {
    slug: "focus-sleep-energy", image: "/goals/focus-sleep-energy.jpg", category: "Cognitive & Energy", name: "Focus, Sleep & Energy",
    tagline: "Nootropic and sleep-cycle peptides in cognition research.",
    blurb: "Semax, Selank and DSIP are studied in neuroprotection, anxiety-model and sleep-regulation research; MOTS-c and 5-Amino-1MQ in cellular energy metabolism.",
    icon: Moon, gradient: "from-[#EBF2F5] to-[#D7E4E9]", border: "#B9CFD8", iconColor: "#3B6E8C", badgeBg: "#E0ECF0", badgeText: "#264D63",
  },
  {
    slug: "libido-vitality", image: "/goals/libido-vitality.jpg", category: "Hormone & Libido", name: "Libido & Vitality",
    tagline: "Peptides studied in hormonal and sexual-function research.",
    blurb: "PT-141 (Bremelanotide) and Kisspeptin are studied for their action on central melanocortin and GnRH pathways. Read the research guides before you browse.",
    icon: HeartPulse, gradient: "from-[#F6EDED] to-[#EACFCE]", border: "#DBB6B5", iconColor: "#8C4B4A", badgeBg: "#EFE1E0", badgeText: "#612E2D",
  },
  {
    slug: "research-supplies", image: "/goals/research-supplies.jpg", category: "Supplies", name: "Research Supplies",
    tagline: "Bacteriostatic water and essentials for reconstitution.",
    blurb: "Everything needed to reconstitute lyophilized peptides in the lab. Pair with our reconstitution calculator to work out concentrations.",
    icon: FlaskConical, gradient: "from-[#F4F4F4] to-[#E5E5E5]", border: "#D1D1D1", iconColor: "#555555", badgeBg: "#EAEAEA", badgeText: "#333333",
  },
];

export const goalBySlug = (slug) => GOALS.find((g) => g.slug === slug);
export const goalForCategory = (category) => GOALS.find((g) => g.category === category);
export const goalName = (category) => goalForCategory(category)?.name || category;

// Families (from groupByName) belonging to a goal, featured first then A–Z.
export const familiesForGoal = (families, goal) =>
  families
    .filter((f) => f.category === goal.category)
    .sort((a, b) => (b.featured - a.featured) || a.name.localeCompare(b.name));

export const popularNames = (families, goal, n = 3) => familiesForGoal(families, goal).slice(0, n).map((f) => f.name);
